import { useState } from 'react';
import { useResearchStore } from '@/stores/researchStore';
import { useAppStore } from '@/stores/appStore';
import { deductCredits, getCreditBalances } from '@/services/credits';
import { FEATURE_FLAGS } from '@/services/api';
import { getResearchStatus, researchContact } from '@/services/research';
import { startCrunchbaseScrape, pollCrunchbaseResults } from '@/services/scraping';
import type { Lead } from '@/types/research';
import { API_CONFIG } from '@/services/api';
import { findMatchingResearchedContact } from '@/utils/researchIdentity';

interface LeadsTableProps {
  onResearchComplete: () => void;
  onResearchLead?: (lead: Lead) => Promise<void>;
  companyMatch?: (lead: Lead) => boolean;
  showCrunchbaseButton?: boolean;
  getDataLabel?: string;
  getDataTitle?: string;
  crunchbaseLabel?: string;
  crunchbaseTitle?: string;
  contactCredits: number;
  setContactCredits: (value: number) => void;
  disableGetData?: boolean;
  selectedReportIds?: Set<string>;
  onToggleReportSelection?: (lead: Lead) => void;
}

type BadgeConfig = {
  label: string;
  tooltip: string;
  bg: string;
  border: string;
  text: string;
};

const BADGE_STYLES: Record<string, BadgeConfig> = {
  'New Hire': {
    label: 'New Hire',
    tooltip: 'New hire at current company.',
    bg: 'bg-amber-900/50',
    border: 'border-amber-700/50',
    text: 'text-amber-300',
  },
  'Promotion': {
    label: 'Promotion',
    tooltip: 'Promotion at current company.',
    bg: 'bg-emerald-900/50',
    border: 'border-emerald-700/50',
    text: 'text-emerald-300',
  },
  'DM': {
    label: 'DM',
    tooltip: 'Decision Maker - they can say yes and sign the check.',
    bg: 'bg-red-900/50',
    border: 'border-red-700/50',
    text: 'text-red-300',
  },
  'DI': {
    label: 'DI',
    tooltip: 'Decision Influencer - they can say yes but need approval.',
    bg: 'bg-blue-900/50',
    border: 'border-blue-700/50',
    text: 'text-blue-300',
  },
  'IC': {
    label: 'IC',
    tooltip: 'Internal Champion - they can say yes, but need coaching to help close.',
    bg: 'bg-gray-800/80',
    border: 'border-gray-600/50',
    text: 'text-gray-400',
  },
};

export function LeadsTable({
  onResearchComplete,
  onResearchLead,
  companyMatch,
  showCrunchbaseButton,
  getDataLabel,
  getDataTitle,
  crunchbaseLabel,
  crunchbaseTitle,
  contactCredits,
  setContactCredits,
  disableGetData = false,
  selectedReportIds,
  onToggleReportSelection,
}: LeadsTableProps) {
  const { userEmail, addScrapeJob, updateScrapeJob } = useAppStore();
  const {
    leads,
    selectedLeads,
    researchedContacts,
    toggleLeadSelection,
    selectAllLeads,
    saveResearchedContact,
  } = useResearchStore();
  const [crunchbaseLoading, setCrunchbaseLoading] = useState<Record<string, boolean>>({});

  const RESEARCH_API_URL = API_CONFIG.RESEARCH_API;
  const defaultGetDataLabel = getDataLabel || 'Get Data';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const decodeHtml = (value: string) => {
    if (!value) return value;
    try {
      const doc = new DOMParser().parseFromString(value, 'text/html');
      return doc.documentElement.textContent || value;
    } catch {
      return value.replace(/&amp;/g, '&');
    }
  };

  const handleResearch = async (lead: Lead, _idx: number, button: HTMLButtonElement) => {
    if (onResearchLead) {
      button.textContent = 'Loading...';
      button.disabled = true;
      try {
        await onResearchLead(lead);
        button.textContent = '✓ Done';
        button.classList.replace('bg-green-600', 'bg-gray-600');
        onResearchComplete();
        } catch (err) {
          console.error(err);
          button.textContent = 'Failed';
          button.classList.replace('bg-green-600', 'bg-red-600');
          setTimeout(() => {
          button.textContent = defaultGetDataLabel;
          button.classList.replace('bg-red-600', 'bg-green-600');
          button.disabled = false;
        }, 2000);
      }
      return;
    }

    if (contactCredits < 1) {
      alert('Insufficient contact credits. Please purchase more.');
      return;
    }
    if (!FEATURE_FLAGS.SERVER_SIDE_CREDITS) {
      try {
        const newCredits = await deductCredits(userEmail, 1, 'get_data', 'contact');
        setContactCredits(newCredits);
      } catch {
        alert('Failed to deduct credit');
        return;
      }
    }

    button.textContent = 'Loading...';
    button.disabled = true;

    try {
      const result = await researchContact(lead, userEmail);
      if (result.success && result.data) {
        saveResearchedContact(lead.searchResultId, result.data);
        const asyncMeta = (result as any).async;
        const asyncStatus = String(asyncMeta?.status ?? '').toLowerCase();
        if ((asyncStatus === 'queued' || asyncStatus === 'retrying') && asyncMeta?.jobId) {
          button.textContent = 'Queued...';
          button.classList.replace('bg-green-600', 'bg-amber-600');
          const pollAfter = Number(asyncMeta?.pollAfterMs ?? 5000);
          const maxPolls = 120;
          let polls = 0;
          const poll = async () => {
            try {
              polls += 1;
              const status = await getResearchStatus(userEmail, String(asyncMeta.jobId));
              if (status.success && status.data) {
                saveResearchedContact(lead.searchResultId, status.data);
                onResearchComplete();
              }
              if (status.done || polls >= maxPolls) {
                button.textContent = status.status === 'completed' ? '✓ Done' : 'Timed Out';
                button.classList.replace('bg-amber-600', status.status === 'completed' ? 'bg-gray-600' : 'bg-red-600');
                button.disabled = status.status === 'completed';
                if (status.status !== 'completed') {
                  setTimeout(() => {
                    button.textContent = defaultGetDataLabel;
                    button.classList.replace('bg-red-600', 'bg-green-600');
                    button.disabled = false;
                  }, 2000);
                }
                try {
                  const refreshed = await getCreditBalances(userEmail);
                  setContactCredits(refreshed.contactCredits);
                } catch { /* ignore */ }
                return;
              }
              const delay = Number(status.nextAttemptAt ? Math.max(1000, new Date(status.nextAttemptAt).getTime() - Date.now()) : pollAfter);
              setTimeout(() => {
                void poll();
              }, delay);
            } catch {
              if (polls >= maxPolls) {
                button.textContent = 'Failed';
                button.classList.replace('bg-amber-600', 'bg-red-600');
                setTimeout(() => {
                  button.textContent = defaultGetDataLabel;
                  button.classList.replace('bg-red-600', 'bg-green-600');
                  button.disabled = false;
                }, 2000);
                return;
              }
              setTimeout(() => {
                void poll();
              }, pollAfter);
            }
          };
          void poll();
        } else {
          button.textContent = '✓ Done';
          button.classList.replace('bg-green-600', 'bg-gray-600');
          onResearchComplete();
          try {
            const refreshed = await getCreditBalances(userEmail);
            setContactCredits(refreshed.contactCredits);
          } catch { /* ignore */ }
        }
      } else {
        console.error('researchContact failed payload', result);
        throw new Error((result as any)?.error || 'No data');
      }
    } catch (err) {
      console.error(err);
      button.textContent = 'Failed';
      button.classList.replace('bg-green-600', 'bg-red-600');
      setTimeout(() => {
        button.textContent = defaultGetDataLabel;
        button.classList.replace('bg-red-600', 'bg-green-600');
        button.disabled = false;
      }, 2000);
      try {
        const refreshed = await getCreditBalances(userEmail);
        setContactCredits(refreshed.contactCredits);
      } catch { /* ignore */ }
    }
  };

  const handleCompanyCrunchbase = async (lead: Lead) => {
    if (!userEmail) return;
    const companyName =
      (typeof lead.company === 'string' ? lead.company : lead.company?.name) ||
      lead.companyName || lead.Company || '';
    if (!companyName || crunchbaseLoading[companyName]) return;

    const jobId = `crunchbase-${companyName}-${Date.now()}`;
    addScrapeJob({
      id: jobId,
      label: `Crunchbase · ${companyName}`,
      source: 'crunchbase',
      companyName,
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: 'Starting...',
    });
    setCrunchbaseLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const start = await startCrunchbaseScrape(companyName, userEmail, true);
      if (!start.success) throw new Error(start.error || 'Crunchbase scrape failed');
      updateScrapeJob(jobId, { status: 'running', progress: 30, message: 'Scraping...' });
      const items = await pollCrunchbaseResults(start, (progress, message) => {
        updateScrapeJob(jobId, { status: 'running', progress, message });
      });
      await fetch(`${RESEARCH_API_URL}/company/crunchbase/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName, data: items }),
      });
      updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: 'Complete' });
    } catch (err) {
      console.error('Crunchbase scrape failed:', err);
      updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed' });
      alert('Crunchbase scrape failed');
    } finally {
      setCrunchbaseLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const getRoleBadge = (seniorityRaw: string): 'DM' | 'DI' | 'IC' | null => {
    const s = (seniorityRaw || '').toLowerCase();
    if (!s) return null;
    if (s.includes('c-level') || s.includes('vp') || s.includes('owner') || s.includes('founder')) return 'DM';
    if (s.includes('director')) return 'DI';
    return 'IC';
  };

  // Days elapsed since a date string (YYYY-MM-DD), null if unparseable
  const daysSince = (dateStr: string | undefined | null): number | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Job Change window: titleStartedAt within this many days = signal
  const JOB_CHANGE_WINDOW_DAYS = 365;

  const buildSignalBadges = (lead: Lead): string[] => {
    const badges: string[] = [];

    const titleAge = daysSince(lead.titleStartedAt);
    const isRecentChange = titleAge !== null && titleAge <= JOB_CHANGE_WINDOW_DAYS;
    const jobChangeAlert = (lead.jobChangeAlert || '').toLowerCase();

    if (isRecentChange) {
      const diff = (lead.jobChangeDifferenceType || '').toLowerCase();
      const currentCompany = (typeof lead.company === 'string' ? lead.company : lead.company?.name || '').trim().toLowerCase();
      const previousCompany = (lead.jobChangeFormerCompany || lead.formerCompany || '').trim().toLowerCase();
      const sameCompany = currentCompany && previousCompany && currentCompany === previousCompany;

      // Promotion: explicit alert OR same company with title change
      const isPromotion =
        jobChangeAlert.includes('promotion') ||
        (sameCompany && (diff.includes('title') || diff.includes('both')));

      if (isPromotion) {
        badges.push('Promotion');
      } else {
        badges.push('New Hire');
      }
    }

    const role = getRoleBadge(lead.seniority || '');
    if (role) badges.push(role);

    return badges;
  };

  const thCls = 'px-3 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-[#0a0a0a] whitespace-nowrap border-b-2 border-gray-700';
  const thCenterCls = `${thCls} text-center`;
  const thIconCls = 'px-1.5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-[#0a0a0a] whitespace-nowrap border-b-2 border-gray-700';
  const tdCls = 'px-3 py-4';
  const tdIconCls = 'px-1.5 py-4';

  return (
    <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
      <table className="w-full text-xs border-collapse">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-gray-800">
            <th className={`${thCls} w-8`}>
              {onToggleReportSelection ? (
                <span className="text-[10px] text-blue-300">Pick</span>
              ) : (
                <input
                  type="checkbox"
                  checked={selectedLeads.size === leads.length && leads.length > 0}
                  onChange={(e) => selectAllLeads(e.target.checked)}
                  className="w-3.5 h-3.5"
                />
              )}
            </th>
            <th className={thCls}>Name</th>
            <th className={thCls}>Title</th>
            <th className={thCls}>Company</th>
            <th className={thCls}>Industry</th>
            <th className={thIconCls}>📱</th>
            <th className={thIconCls}>📞</th>
            <th className={thIconCls}>📧 Biz</th>
            <th className={thIconCls}>📧 Pers</th>
            <th className={thIconCls}>in</th>
            <th className={thCls}>Contact Loc.</th>
            <th className={thCls}>Company Loc.</th>
            <th className={thCenterCls}>
              {showCrunchbaseButton ? (crunchbaseLabel || 'Crunchbase') : 'Signals'}
            </th>
            <th className={thCenterCls}>{disableGetData ? 'Preview' : (getDataLabel || 'Get Data')}</th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 ? (
            <tr>
              <td colSpan={14} className="px-6 py-16 text-center">
                <div className="flex flex-col items-center gap-2 text-gray-600">
                  <svg className="w-8 h-8 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-500">No results yet</p>
                  <p className="text-xs text-gray-700">Adjust your filters and search to find leads</p>
                </div>
              </td>
            </tr>
          ) : (
            leads.map((lead, idx) => {
              const cached = findMatchingResearchedContact(lead, researchedContacts);
              const firstName = lead.firstName || (lead.name ? lead.name.split(' ')[0] : '') || '';
              const lastName = lead.lastName || (lead.name ? lead.name.split(' ').slice(1).join(' ') : '') || '';
              const title = lead.title || '';
              const company =
                typeof lead.company === 'string'
                  ? lead.company
                  : lead.company?.name || lead.companyName || lead.Company || '';

              // Industry: up to two entries joined with " & ", rest truncated
              const industryList: string[] = Array.isArray((lead as any).industries) && (lead as any).industries.length > 0
                ? (lead as any).industries
                : [((typeof lead.company === 'string' ? '' : lead.company?.industry) || lead.industry || '')].filter(Boolean);
              const decodedIndustries = industryList.map((i: string) => decodeHtml(i));
              const industryDisplay = decodedIndustries.length === 0 ? '—'
                : decodedIndustries.length === 1 ? decodedIndustries[0]
                : decodedIndustries.length === 2 ? `${decodedIndustries[0]} & ${decodedIndustries[1]}`
                : `${decodedIndustries[0]} & ${decodedIndustries[1]} +${decodedIndustries.length - 2}`;
              const contactLocationTop = lead.city || '';
              const contactLocationBottom = [lead.state, lead.country].filter(Boolean).join(', ');
              const companyLocationTop = lead.companyCity || '';
              const companyLocationBottom = [lead.companyState, lead.companyCountry].filter(Boolean).join(', ');
              const mobile = lead.contactPhone1 || cached?.contactPhone1 || '';
              const bizPhone = lead.contactPhone2 || cached?.companyPhone1 || '';
              const bizEmail = lead.businessEmail1 || lead.email || cached?.Email || '';
              const persEmail = lead.personalEmail1 || cached?.PersonalEmail || '';
              const linkedin = lead.linkedInUrl || cached?.LIProfileUrl || '';
              const badges = buildSignalBadges(lead);
              const companyKey = (typeof lead.company === 'string' ? lead.company : lead.company?.name) || lead.companyName || lead.Company || '';

              return (
                <tr key={lead.searchResultId} className="border-b border-gray-800/50 odd:bg-transparent even:bg-white/[0.015] hover:bg-blue-950/20 transition-colors group">
                  <td className={`${tdCls} text-center`}>
                    {onToggleReportSelection ? (
                      <button
                        type="button"
                        onClick={() => onToggleReportSelection(lead)}
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                          selectedReportIds?.has(lead.searchResultId)
                            ? 'bg-blue-700/40 border-blue-500 text-blue-200'
                            : 'bg-gray-800/80 border-gray-700 text-gray-300'
                        }`}
                      >
                        {selectedReportIds?.has(lead.searchResultId) ? 'Added' : 'Add'}
                      </button>
                    ) : (
                      <input
                        type="checkbox"
                        checked={selectedLeads.has(lead.searchResultId)}
                        onChange={() => toggleLeadSelection(lead.searchResultId)}
                        className="w-3.5 h-3.5 lead-checkbox"
                      />
                    )}
                  </td>
                  <td className={`${tdCls} text-white`}>
                    <div className="leading-tight">
                      <div className="font-semibold text-white">{decodeHtml(firstName)}</div>
                      <div className="font-normal text-gray-300">{decodeHtml(lastName)}</div>
                    </div>
                  </td>
                  <td className={`${tdCls} text-gray-300 max-w-[170px]`}>
                    <div className="truncate text-[11px] leading-snug" title={decodeHtml(title)}>{decodeHtml(title)}</div>
                  </td>
                  <td className={`${tdCls} text-gray-300 max-w-[150px]`}>
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-[11px]" title={decodeHtml(company)}>{decodeHtml(company)}</span>
                      {companyMatch?.(lead) && (
                        <span className="shrink-0 text-emerald-300 text-[9px] uppercase tracking-wide bg-emerald-900/40 border border-emerald-700/40 px-1.5 py-0.5 rounded-full">
                          ✓
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`${tdCls} text-gray-400 max-w-[150px]`}>
                    <div
                      className="text-[11px] leading-snug"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      title={industryDisplay}
                    >
                      {industryDisplay}
                    </div>
                  </td>

                  {/* Contact data cells */}
                  {[
                    { val: mobile, label: 'Mobile' },
                    { val: bizPhone, label: 'Biz Phone' },
                    { val: bizEmail, label: 'Biz Email' },
                    { val: persEmail, label: 'Pers Email' },
                  ].map(({ val, label }) => (
                    <td key={label} className={`${tdIconCls} text-center`}>
                      {val ? (
                        <button
                          onClick={() => copyToClipboard(val)}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-900/50 border border-green-700/40 text-green-400 hover:bg-green-800/60 hover:text-green-300 text-[10px] font-bold transition-colors"
                          title={val}
                        >
                          ✓
                        </button>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-800/30 text-gray-700 text-[10px]">✗</span>
                      )}
                    </td>
                  ))}

                  <td className={`${tdIconCls} text-center`}>
                    {linkedin ? (
                      <a href={linkedin} target="_blank" rel="noopener noreferrer">
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/174/174857.png"
                          className="w-4 h-4 inline opacity-60 hover:opacity-100 transition-opacity"
                          alt="LinkedIn"
                        />
                      </a>
                    ) : (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-800/30 text-gray-700 text-[10px]">✗</span>
                    )}
                  </td>

                  <td className={`${tdCls} text-gray-400`}>
                    <div className="leading-tight">
                      <div>{decodeHtml(contactLocationTop || contactLocationBottom || '—')}</div>
                      {contactLocationTop && contactLocationBottom && (
                        <div className="text-[10px] text-gray-500">{decodeHtml(contactLocationBottom)}</div>
                      )}
                    </div>
                  </td>
                  <td className={`${tdCls} text-gray-400`}>
                    <div className="leading-tight">
                      <div>{decodeHtml(companyLocationTop || companyLocationBottom || '—')}</div>
                      {companyLocationTop && companyLocationBottom && (
                        <div className="text-[10px] text-gray-500">{decodeHtml(companyLocationBottom)}</div>
                      )}
                    </div>
                  </td>

                  {/* Badges / Crunchbase */}
                  <td className={`${tdCls} text-center`}>
                    {showCrunchbaseButton ? (
                      <button
                        onClick={() => handleCompanyCrunchbase(lead)}
                        disabled={!cached || !!crunchbaseLoading[companyKey]}
                        className={`px-2 py-0.5 rounded text-xs ${cached ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-gray-800 text-gray-600 cursor-not-allowed'}`}
                        title={cached ? (crunchbaseTitle || 'Run Crunchbase (2 credits)') : 'Run Get Data first'}
                      >
                        {crunchbaseLoading[companyKey] ? '...' : (crunchbaseLabel || 'CB')}
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-center justify-center gap-0.5 max-w-[90px] mx-auto min-h-[2.5rem]">
                        {badges.slice(0, 3).map((badge) => {
                          const style = BADGE_STYLES[badge] || BADGE_STYLES['IC'];
                          return (
                            <span
                              key={`${lead.searchResultId}-${badge}`}
                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${style.bg} ${style.border} ${style.text}`}
                              title={style.tooltip}
                            >
                              {badge}
                            </span>
                          );
                        })}
                        {badges.length > 3 && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border bg-gray-800/80 border-gray-600/50 text-gray-400" title={badges.slice(3).join(', ')}>
                            +{badges.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Get Data */}
                  <td className={`${tdCls} text-center`}>
                    {disableGetData ? (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-800/80 border border-gray-700 text-gray-400">
                        Locked
                      </span>
                    ) : cached ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-900/50 border border-green-700/40 text-green-400 text-[10px] font-bold">✓</span>
                    ) : (
                      <button
                        onClick={(e) => handleResearch(lead, idx, e.currentTarget)}
                        className="btn-research bg-[#3B82F6] hover:bg-blue-500 text-white px-2.5 py-0.5 rounded text-xs font-medium transition-colors"
                        title={getDataTitle || 'Get Data'}
                      >
                        {getDataLabel || 'Get Data'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
